import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  SwipeableDialog as Dialog,
  SwipeableDialogContent as DialogContent,
  SwipeableDialogHeader as DialogHeader,
  SwipeableDialogTitle as DialogTitle,
} from "@/components/ui/swipeable-dialog";
import { 
    ArrowLeft, Calendar, Clock, Users, Euro, Check, X,
    ChevronLeft, ChevronRight, Building2, AlertCircle, LogIn, Info, Plus, Minus, Gift
} from 'lucide-react';
import { toast } from "sonner";
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate time slots based on interval (15 or 60 minutes)
const generateTimeSlots = (intervalMinutes = 30) => {
    const slots = [];
    for (let hour = 8; hour <= 20; hour++) {
        for (let minute = 0; minute < 60; minute += intervalMinutes) {
            if (hour === 20 && minute > 0) break; // Stop at 20:00
            slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        }
    }
    return slots;
};

// Default 30-min slots (legacy)
const TIME_SLOTS = generateTimeSlots(30);
// 15-min slots for internal users
const TIME_SLOTS_15MIN = generateTimeSlots(15);
// 60-min slots for external users
const TIME_SLOTS_60MIN = generateTimeSlots(60);

const DAYS_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export const RoomBookingPage = () => {
    const { roomId } = useParams();
    const navigate = useNavigate();
    
    const [room, setRoom] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [addons, setAddons] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Auth state
    const [user, setUser] = useState(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('login'); // login or register
    const [authForm, setAuthForm] = useState({ email: '', password: '', first_name: '', last_name: '', company: '' });
    
    // Addon info popup state
    const [selectedAddonInfo, setSelectedAddonInfo] = useState(null);
    
    // Booking form state
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [selectedBookingOption, setSelectedBookingOption] = useState(''); // 'half_day' or 'full_day' for Seeblick external
    const [participants, setParticipants] = useState(1);
    const [purpose, setPurpose] = useState('');
    const [selectedAddons, setSelectedAddons] = useState([]); // Now stores {id, quantity}
    const [addonQuantities, setAddonQuantities] = useState({}); // {addonId: quantity}
    const [specialRequests, setSpecialRequests] = useState('');
    const [guestInfo, setGuestInfo] = useState({ name: '', email: '', phone: '' });
    
    // Discount code state
    const [discountCode, setDiscountCode] = useState('');
    const [discountValidation, setDiscountValidation] = useState(null); // {valid, discount_percent, discount_amount, message}
    const [validatingDiscount, setValidatingDiscount] = useState(false);
    
    const [submitting, setSubmitting] = useState(false);
    const [cancellationRules, setCancellationRules] = useState(null);

    useEffect(() => {
        fetchRoom();
        fetchBookings();
        fetchAddons();
        fetchCancellationRules();
        checkAuth();
    }, [roomId]);

    const fetchCancellationRules = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/cancellation-rules`);
            setCancellationRules(response.data);
        } catch (error) {
            console.error('Error fetching cancellation rules:', error);
        }
    };

    const checkAuth = async () => {
        const token = localStorage.getItem('booking_token');
        if (!token) return;
        
        try {
            const response = await axios.get(`${API_URL}/api/booking/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            localStorage.removeItem('booking_token');
        }
    };

    const fetchRoom = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/rooms/${roomId}`);
            setRoom(response.data);
        } catch (error) {
            toast.error("Raum nicht gefunden");
            navigate('/buchung');
        } finally {
            setLoading(false);
        }
    };

    const fetchBookings = async () => {
        try {
            // Use calendar endpoint for privacy-protected booking data
            const token = localStorage.getItem('booking_token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(
                `${API_URL}/api/booking/bookings/room/${roomId}/calendar`,
                { headers }
            );
            setBookings(response.data);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            // Fallback to regular endpoint without auth
            try {
                const response = await axios.get(`${API_URL}/api/booking/bookings/room/${roomId}`);
                setBookings(response.data);
            } catch (e) {
                console.error('Fallback also failed:', e);
            }
        }
    };

    const fetchAddons = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/booking/addons`);
            setAddons(response.data);
        } catch (error) {
            console.error('Error fetching addons:', error);
        }
    };

    // Calculate addon price based on type and quantity
    const calculateAddonPrice = (addon, quantity = 1) => {
        if (user?.role === 'internal' || user?.role === 'admin') return 0;
        
        if (addon.price_type === 'tiered' && addon.price_tiers?.length > 0) {
            // Find applicable tier (sorted descending by min_quantity)
            const sortedTiers = [...addon.price_tiers].sort((a, b) => b.min_quantity - a.min_quantity);
            for (const tier of sortedTiers) {
                if (quantity >= tier.min_quantity && (tier.max_quantity === null || quantity <= tier.max_quantity)) {
                    return quantity * tier.price_per_unit;
                }
            }
            return quantity * addon.price;
        } else if (addon.price_type === 'per_person') {
            return addon.price * participants;
        } else if (addon.price_type === 'per_unit') {
            return addon.price * quantity;
        }
        return addon.price; // flat
    };

    // Get price per unit for display (for tiered pricing)
    const getAddonPricePerUnit = (addon, quantity = 1) => {
        if (addon.price_type === 'tiered' && addon.price_tiers?.length > 0) {
            const sortedTiers = [...addon.price_tiers].sort((a, b) => b.min_quantity - a.min_quantity);
            for (const tier of sortedTiers) {
                if (quantity >= tier.min_quantity && (tier.max_quantity === null || quantity <= tier.max_quantity)) {
                    return tier.price_per_unit;
                }
            }
        }
        return addon.price;
    };

    // Handle addon selection with quantity
    const toggleAddon = (addonId, checked) => {
        if (checked) {
            setSelectedAddons([...selectedAddons, addonId]);
            setAddonQuantities(prev => ({ ...prev, [addonId]: 1 }));
        } else {
            setSelectedAddons(selectedAddons.filter(id => id !== addonId));
            setAddonQuantities(prev => {
                const newQty = { ...prev };
                delete newQty[addonId];
                return newQty;
            });
        }
    };

    const updateAddonQuantity = (addonId, quantity) => {
        const addon = addons.find(a => a._id === addonId);
        const minQty = addon?.min_quantity || 1;
        const maxQty = addon?.max_quantity || 100;
        const newQty = Math.max(minQty, Math.min(maxQty, quantity));
        setAddonQuantities(prev => ({ ...prev, [addonId]: newQty }));
    };

    // Handle Auth
    const handleAuth = async (e) => {
        e.preventDefault();
        try {
            let response;
            if (authMode === 'login') {
                response = await axios.post(`${API_URL}/api/booking/auth/login`, {
                    email: authForm.email,
                    password: authForm.password
                });
            } else {
                response = await axios.post(`${API_URL}/api/booking/auth/register`, authForm);
            }
            
            localStorage.setItem('booking_token', response.data.access_token);
            // Dispatch custom event to notify Navbar immediately
            window.dispatchEvent(new Event('authChange'));
            setUser(response.data.user);
            setShowAuthModal(false);
            toast.success(authMode === 'login' ? 'Erfolgreich eingeloggt' : 'Erfolgreich registriert');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler bei der Anmeldung');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('booking_token');
        // Dispatch custom event to notify Navbar immediately
        window.dispatchEvent(new Event('authChange'));
        setUser(null);
        toast.success('Erfolgreich abgemeldet');
    };

    // Validate discount code
    const validateDiscountCode = async () => {
        if (!discountCode.trim()) return;
        
        setValidatingDiscount(true);
        try {
            // Calculate room price directly
            let roomPrice = 0;
            if (startTime && endTime && room && !(user?.role === 'internal' || user?.role === 'admin')) {
                const start = new Date(`2000-01-01T${startTime}:00`);
                const end = new Date(`2000-01-01T${endTime}:00`);
                const hours = (end - start) / (1000 * 60 * 60);
                if (hours > 0) {
                    roomPrice = hours * (room.price_per_hour || 0);
                }
            }
            
            const response = await axios.post(`${API_URL}/api/booking/validate-discount`, {
                code: discountCode.trim(),
                room_price: roomPrice
            });
            
            setDiscountValidation(response.data);
            toast.success(`✅ ${response.data.message}`);
        } catch (error) {
            setDiscountValidation(null);
            toast.error(error.response?.data?.detail || 'Ungültiger Rabattcode');
        } finally {
            setValidatingDiscount(false);
        }
    };

    // Check if a time slot is available
    const isSlotBooked = (date, time) => {
        const dateStr = date.toISOString().split('T')[0];
        const slotStart = new Date(`${dateStr}T${time}:00`);
        
        return bookings.some(booking => {
            const bookingStart = new Date(booking.start_time);
            const bookingEnd = new Date(booking.end_time);
            return slotStart >= bookingStart && slotStart < bookingEnd;
        });
    };

    // Get opening hours for selected day
    const getOpeningHours = () => {
        if (!room?.opening_hours) return { is_open: true, start_time: '08:00', end_time: '20:00' };
        const dayIdx = selectedDate.getDay();
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const schedule = room.opening_hours.find(h => h.day === dayNames[dayIdx]);
        return schedule || { is_open: true, start_time: '08:00', end_time: '20:00' };
    };

    // Determine if this is Seeblick room (external users can only book half/full day)
    const isSeeblickRoom = room?.name?.toLowerCase().includes('seeblick');
    
    // Check if user is internal/admin (gets free booking and 15-min slots)
    const isInternalUser = user?.role === 'internal' || user?.role === 'admin';
    
    // External users on Seeblick must use booking options (half/full day)
    const requiresBookingOption = isSeeblickRoom && !isInternalUser;

    // Get appropriate time slots based on user role and room
    const getAvailableTimeSlots = () => {
        if (isInternalUser) {
            return TIME_SLOTS_15MIN; // Internal users: 15-min slots
        }
        return TIME_SLOTS_60MIN; // External users: 60-min slots
    };

    // Calculate room price only (without addons)
    const roomOnlyPrice = useMemo(() => {
        // Internal users always free
        if (isInternalUser) return 0;
        
        // Seeblick external: use fixed half/full day prices
        if (requiresBookingOption) {
            if (selectedBookingOption === 'half_day') {
                return room?.external_half_day_price || 249;
            } else if (selectedBookingOption === 'full_day') {
                return room?.external_full_day_price || 449;
            }
            return 0; // No option selected yet
        }
        
        // Waldblick external: hourly pricing
        if (!startTime || !endTime || !room) return 0;
        
        const start = new Date(`2000-01-01T${startTime}:00`);
        const end = new Date(`2000-01-01T${endTime}:00`);
        const hours = (end - start) / (1000 * 60 * 60);
        
        if (hours <= 0) return 0;
        
        return hours * (room.price_per_hour || 0);
    }, [startTime, endTime, room, user, isInternalUser, requiresBookingOption, selectedBookingOption]);

    // Calculate addon total
    const addonsTotal = useMemo(() => {
        if (user?.role === 'internal' || user?.role === 'admin') return 0;
        
        let total = 0;
        selectedAddons.forEach(addonId => {
            const addon = addons.find(a => a._id === addonId);
            if (addon) {
                const quantity = addonQuantities[addonId] || 1;
                total += calculateAddonPrice(addon, quantity);
            }
        });
        return total;
    }, [user, selectedAddons, addons, addonQuantities, participants]);

    // Calculate total price (room + addons)
    const calculatedPrice = useMemo(() => {
        return roomOnlyPrice + addonsTotal;
    }, [roomOnlyPrice, addonsTotal]);

    // Calculate final net price (after discount)
    const finalNetPrice = useMemo(() => {
        const discountAmount = discountValidation?.valid ? discountValidation.discount_amount : 0;
        return calculatedPrice - discountAmount;
    }, [calculatedPrice, discountValidation]);

    // Generate calendar days
    const calendarDays = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const days = [];
        
        // Add empty days for the start
        for (let i = 0; i < (firstDay.getDay() || 7) - 1; i++) {
            days.push(null);
        }
        
        // Add days of month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }
        
        return days;
    }, [currentMonth]);

    // Handle booking submission
    const handleSubmit = async () => {
        // Validate for Seeblick external users
        if (requiresBookingOption && !selectedBookingOption) {
            toast.error('Bitte wählen Sie eine Buchungsoption (Halber Tag oder Ganzer Tag)');
            return;
        }
        
        if (!startTime || !endTime) {
            toast.error('Bitte Uhrzeit auswählen');
            return;
        }
        
        if (!user && (!guestInfo.name || !guestInfo.email)) {
            toast.error('Bitte Name und E-Mail eingeben');
            return;
        }
        
        const dateStr = selectedDate.toISOString().split('T')[0];
        
        setSubmitting(true);
        try {
            const token = localStorage.getItem('booking_token');
            const response = await axios.post(
                `${API_URL}/api/booking/bookings`,
                {
                    room_id: roomId,
                    start_time: `${dateStr}T${startTime}:00Z`,
                    end_time: `${dateStr}T${endTime}:00Z`,
                    participants_count: participants,
                    purpose: purpose,
                    booking_option: requiresBookingOption ? selectedBookingOption : undefined,
                    addons: selectedAddons.map(id => ({ 
                        id, 
                        quantity: addonQuantities[id] || 1 
                    })),
                    special_requests: specialRequests || undefined,
                    discount_code: discountValidation?.valid ? discountCode.trim() : undefined,
                    guest_name: !user ? guestInfo.name : undefined,
                    guest_email: !user ? guestInfo.email : undefined,
                    guest_phone: !user ? guestInfo.phone : undefined
                },
                token ? { headers: { Authorization: `Bearer ${token}` } } : {}
            );
            
            const booking = response.data;
            
            // Check if payment is required (external user with price > 0)
            if (booking.status === 'pending' && booking.total_price > 0) {
                // Redirect to Stripe checkout
                const checkoutResponse = await axios.post(`${API_URL}/api/booking/checkout`, {
                    booking_id: booking._id,
                    success_url: `${window.location.origin}/buchung/success`,
                    cancel_url: `${window.location.origin}/buchung/cancel`
                });
                
                // Redirect to Stripe
                window.location.href = checkoutResponse.data.checkout_url;
            } else {
                // Booking confirmed (internal user or free booking)
                toast.success('Buchung erfolgreich bestätigt!');
                navigate('/buchung/success?free=true');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler bei der Buchung');
            setSubmitting(false);
        }
    };

    const openingHours = getOpeningHours();

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (!room) return null;

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            {/* Header with black background */}
            <header className="bg-black pt-28 pb-8 px-4">
                <div className="max-w-6xl mx-auto">
                    <button 
                        onClick={() => navigate('/buchung')}
                        className="flex items-center gap-2 text-gray-300 hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        Zurück zur Übersicht
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-white">{room.name}</h1>
                        <p className="text-gray-300">{room.description}</p>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="max-w-6xl mx-auto py-8 px-4">
                <div className="grid lg:grid-cols-3 gap-8">
                    
                    {/* Left: Calendar & Time */}
                    <div className="lg:col-span-2 space-y-6">
                        
                        {/* User Auth Bar */}
                        <Card>
                            <CardContent className="p-4">
                                {user ? (
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <span className="text-sm text-gray-500">Eingeloggt als:</span>
                                            <div className="font-semibold">{user.first_name} {user.last_name}</div>
                                            {user.role === 'internal' && (
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                    Interner Nutzer - Kostenlose Buchung
                                                </span>
                                            )}
                                        </div>
                                        <Button variant="outline" size="sm" onClick={handleLogout}>
                                            Abmelden
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-500">
                                            Einloggen für schnellere Buchung oder als interner Nutzer
                                        </span>
                                        <Button size="sm" onClick={() => setShowAuthModal(true)}>
                                            <LogIn size={16} className="mr-1" />
                                            Anmelden
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Calendar */}
                        <Card>
                            <CardContent className="p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold flex items-center gap-2">
                                        <Calendar size={20} />
                                        Datum wählen
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                                            className="p-1 hover:bg-gray-100 rounded"
                                        >
                                            <ChevronLeft size={20} />
                                        </button>
                                        <span className="font-medium min-w-[150px] text-center">
                                            {MONTHS_DE[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                                        </span>
                                        <button 
                                            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                                            className="p-1 hover:bg-gray-100 rounded"
                                        >
                                            <ChevronRight size={20} />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-7 gap-1 mb-2">
                                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                                        <div key={day} className="text-center text-sm text-gray-500 py-2">{day}</div>
                                    ))}
                                </div>
                                
                                <div className="grid grid-cols-7 gap-1">
                                    {calendarDays.map((day, i) => {
                                        if (!day) return <div key={i} />;
                                        
                                        const isSelected = day.toDateString() === selectedDate.toDateString();
                                        const isPast = day < new Date(new Date().setHours(0,0,0,0));
                                        const dayIdx = day.getDay();
                                        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                                        const schedule = room.opening_hours?.find(h => h.day === dayNames[dayIdx]);
                                        const isClosed = schedule && !schedule.is_open;
                                        
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => !isPast && !isClosed && setSelectedDate(day)}
                                                disabled={isPast || isClosed}
                                                className={`
                                                    p-2 text-center rounded transition-colors
                                                    ${isSelected ? 'bg-black text-white' : ''}
                                                    ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                                                    ${isClosed ? 'text-red-300 cursor-not-allowed line-through' : ''}
                                                    ${!isSelected && !isPast && !isClosed ? 'hover:bg-gray-100' : ''}
                                                `}
                                            >
                                                {day.getDate()}
                                            </button>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Time Selection */}
                        <Card>
                            <CardContent className="p-6">
                                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                                    <Clock size={20} />
                                    {requiresBookingOption ? 'Buchungsoption wählen' : 'Uhrzeit wählen'}
                                </h3>
                                
                                {!openingHours.is_open ? (
                                    <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2">
                                        <AlertCircle size={20} />
                                        Der Raum ist an diesem Tag geschlossen.
                                    </div>
                                ) : requiresBookingOption ? (
                                    /* Seeblick External Users: Half/Full Day Options */
                                    <>
                                        <p className="text-sm text-gray-500 mb-4">
                                            Der Raum Seeblick kann von externen Nutzern nur halb- oder ganztags gebucht werden.
                                        </p>
                                        
                                        <div className="space-y-3">
                                            {/* Half Day Option */}
                                            <label 
                                                className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                                    selectedBookingOption === 'half_day' 
                                                        ? 'border-black bg-gray-50' 
                                                        : 'border-gray-200 hover:border-gray-400'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="radio"
                                                        name="bookingOption"
                                                        value="half_day"
                                                        checked={selectedBookingOption === 'half_day'}
                                                        onChange={(e) => {
                                                            setSelectedBookingOption(e.target.value);
                                                            setStartTime('08:00');
                                                            setEndTime('12:00');
                                                        }}
                                                        className="w-4 h-4"
                                                    />
                                                    <div>
                                                        <div className="font-semibold">Halber Tag</div>
                                                        <div className="text-sm text-gray-500">4 Stunden (z.B. 08:00 - 12:00 oder 13:00 - 17:00)</div>
                                                    </div>
                                                </div>
                                                <div className="text-xl font-bold">{room?.external_half_day_price || 249}€</div>
                                            </label>
                                            
                                            {/* Full Day Option */}
                                            <label 
                                                className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                                    selectedBookingOption === 'full_day' 
                                                        ? 'border-black bg-gray-50' 
                                                        : 'border-gray-200 hover:border-gray-400'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="radio"
                                                        name="bookingOption"
                                                        value="full_day"
                                                        checked={selectedBookingOption === 'full_day'}
                                                        onChange={(e) => {
                                                            setSelectedBookingOption(e.target.value);
                                                            setStartTime('08:00');
                                                            setEndTime('18:00');
                                                        }}
                                                        className="w-4 h-4"
                                                    />
                                                    <div>
                                                        <div className="font-semibold">Ganzer Tag</div>
                                                        <div className="text-sm text-gray-500">8 Stunden (z.B. 08:00 - 18:00)</div>
                                                    </div>
                                                </div>
                                                <div className="text-xl font-bold">{room?.external_full_day_price || 449}€</div>
                                            </label>
                                        </div>
                                        
                                        {/* Time preference for booking option */}
                                        {selectedBookingOption && (
                                            <div className="mt-4 pt-4 border-t">
                                                <label className="text-sm font-medium block mb-2">Gewünschte Startzeit</label>
                                                <select 
                                                    className="w-full border rounded-lg p-3"
                                                    value={startTime}
                                                    onChange={(e) => {
                                                        const newStart = e.target.value;
                                                        setStartTime(newStart);
                                                        // Auto-calculate end time based on option
                                                        const startHour = parseInt(newStart.split(':')[0]);
                                                        const duration = selectedBookingOption === 'half_day' ? 4 : 8;
                                                        const endHour = Math.min(startHour + duration, 20);
                                                        setEndTime(`${endHour.toString().padStart(2, '0')}:00`);
                                                    }}
                                                >
                                                    <option value="">Startzeit wählen</option>
                                                    {TIME_SLOTS_60MIN.filter(t => {
                                                        const hour = parseInt(t.split(':')[0]);
                                                        const duration = selectedBookingOption === 'half_day' ? 4 : 8;
                                                        return t >= openingHours.start_time && (hour + duration) <= 20;
                                                    }).map(time => (
                                                        <option key={time} value={time}>{time}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    /* Regular Time Selection (Waldblick or Internal Users) */
                                    <>
                                        <p className="text-sm text-gray-500 mb-4">
                                            Öffnungszeiten: {openingHours.start_time} - {openingHours.end_time} Uhr
                                            {isInternalUser && (
                                                <span className="ml-2 text-green-600 font-medium">
                                                    (15-Min-Slots verfügbar)
                                                </span>
                                            )}
                                        </p>
                                        
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Von</label>
                                                <select 
                                                    className="w-full border rounded-lg p-3"
                                                    value={startTime}
                                                    onChange={(e) => setStartTime(e.target.value)}
                                                >
                                                    <option value="">Startzeit wählen</option>
                                                    {getAvailableTimeSlots().filter(t => t >= openingHours.start_time && t < openingHours.end_time).map(time => (
                                                        <option 
                                                            key={time} 
                                                            value={time}
                                                            disabled={isSlotBooked(selectedDate, time)}
                                                        >
                                                            {time} {isSlotBooked(selectedDate, time) ? '(belegt)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Bis</label>
                                                <select 
                                                    className="w-full border rounded-lg p-3"
                                                    value={endTime}
                                                    onChange={(e) => setEndTime(e.target.value)}
                                                >
                                                    <option value="">Endzeit wählen</option>
                                                    {getAvailableTimeSlots().filter(t => t > startTime && t <= openingHours.end_time).map(time => (
                                                        <option key={time} value={time}>{time}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Teilnehmer</label>
                                                <Input 
                                                    type="number"
                                                    min={1}
                                                    max={room.capacity}
                                                    value={participants}
                                                    onChange={(e) => setParticipants(Math.min(parseInt(e.target.value) || 1, room.capacity))}
                                                />
                                                <span className="text-xs text-gray-500">max. {room.capacity}</span>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Zweck (optional)</label>
                                                <Input 
                                                    placeholder="z.B. Team Meeting"
                                                    value={purpose}
                                                    onChange={(e) => setPurpose(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Bookings for selected day */}
                                        {bookings.filter(b => {
                                            const bStart = new Date(b.start_time);
                                            return bStart.toDateString() === selectedDate.toDateString();
                                        }).length > 0 && (
                                            <div className="mt-4 pt-4 border-t">
                                                <h4 className="text-sm font-medium text-gray-700 mb-2">
                                                    Buchungen am {selectedDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}:
                                                </h4>
                                                <div className="space-y-2">
                                                    {bookings
                                                        .filter(b => {
                                                            const bStart = new Date(b.start_time);
                                                            return bStart.toDateString() === selectedDate.toDateString();
                                                        })
                                                        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                                                        .map(booking => {
                                                            const bStart = new Date(booking.start_time);
                                                            const bEnd = new Date(booking.end_time);
                                                            const isOwn = booking.is_own;
                                                            
                                                            return (
                                                                <div 
                                                                    key={booking._id} 
                                                                    className={`p-2 rounded text-sm ${
                                                                        isOwn 
                                                                            ? 'bg-green-50 border border-green-200' 
                                                                            : 'bg-gray-50 border border-gray-200'
                                                                    }`}
                                                                >
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="font-medium">
                                                                            {bStart.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} - {bEnd.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                                                        </span>
                                                                        {isOwn && (
                                                                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                                                                Ihre Buchung
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-gray-600">
                                                                        {isOwn ? (
                                                                            <>
                                                                                {booking.purpose || 'Keine Angabe'}
                                                                                {booking.participants_count && ` • ${booking.participants_count} Personen`}
                                                                            </>
                                                                        ) : (
                                                                            <span className="italic">Belegt</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        {/* Guest Info (if not logged in) */}
                        {!user && (
                            <Card>
                                <CardContent className="p-6">
                                    <h3 className="text-lg font-semibold mb-4">Ihre Kontaktdaten</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium block mb-1">Name *</label>
                                            <Input 
                                                value={guestInfo.name}
                                                onChange={(e) => setGuestInfo({...guestInfo, name: e.target.value})}
                                                placeholder="Max Mustermann"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium block mb-1">E-Mail *</label>
                                            <Input 
                                                type="email"
                                                value={guestInfo.email}
                                                onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                                                placeholder="max@beispiel.de"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-sm font-medium block mb-1">Telefon (optional)</label>
                                            <Input 
                                                value={guestInfo.phone}
                                                onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
                                                placeholder="+49 123 456789"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Right: Summary */}
                    <div className="space-y-6">
                        <Card className="sticky top-4">
                            <CardContent className="p-6">
                                <h3 className="text-lg font-semibold mb-4">Buchungsübersicht</h3>
                                
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Raum</span>
                                        <span className="font-medium">{room.name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Datum</span>
                                        <span className="font-medium">
                                            {selectedDate.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                                        </span>
                                    </div>
                                    {requiresBookingOption && selectedBookingOption && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Buchungsoption</span>
                                            <span className="font-medium">
                                                {selectedBookingOption === 'half_day' ? 'Halber Tag' : 'Ganzer Tag'}
                                            </span>
                                        </div>
                                    )}
                                    {startTime && endTime && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Uhrzeit</span>
                                            <span className="font-medium">{startTime} - {endTime} Uhr</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Teilnehmer</span>
                                        <span className="font-medium">{participants}</span>
                                    </div>
                                </div>

                                {/* Addons with Info Button and Quantity */}
                                {addons.length > 0 && (
                                    <div className="mt-6 pt-4 border-t">
                                        <h4 className="font-medium mb-3">Zusatzleistungen</h4>
                                        <div className="space-y-3">
                                            {addons.map(addon => {
                                                const isSelected = selectedAddons.includes(addon._id);
                                                const quantity = addonQuantities[addon._id] || 1;
                                                const hasTieredPricing = addon.price_type === 'tiered' && addon.price_tiers?.length > 0;
                                                const hasQuantity = addon.price_type === 'tiered' || addon.price_type === 'per_unit';
                                                const currentPricePerUnit = getAddonPricePerUnit(addon, quantity);
                                                
                                                return (
                                                    <div key={addon._id} className="border rounded-lg p-3 hover:border-gray-400 transition-colors">
                                                        <div className="flex items-start gap-3">
                                                            <input 
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={(e) => toggleAddon(addon._id, e.target.checked)}
                                                                className="mt-1 w-4 h-4"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-medium text-sm">{addon.name}</span>
                                                                    {addon.detailed_description && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                setSelectedAddonInfo(addon);
                                                                            }}
                                                                            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                                                                            title="Mehr Informationen"
                                                                        >
                                                                            <Info size={16} className="text-gray-500" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-0.5">{addon.description}</div>
                                                                
                                                                {/* Price Display */}
                                                                <div className="text-sm mt-1">
                                                                    {addon.price_type === 'per_person' ? (
                                                                        <span className="text-gray-700">{addon.price}€ / Person</span>
                                                                    ) : hasTieredPricing ? (
                                                                        <span className="text-gray-700">
                                                                            {currentPricePerUnit}€ / {addon.unit_name || 'Stück'}
                                                                            {quantity >= 10 && <span className="text-green-600 ml-1">(Mengenrabatt!)</span>}
                                                                        </span>
                                                                    ) : addon.price_type === 'per_unit' ? (
                                                                        <span className="text-gray-700">{addon.price}€ / {addon.unit_name || 'Stück'}</span>
                                                                    ) : (
                                                                        <span className="text-gray-700">{addon.price}€</span>
                                                                    )}
                                                                </div>

                                                                {/* Tiered Pricing Info */}
                                                                {hasTieredPricing && (
                                                                    <div className="text-xs text-gray-400 mt-1">
                                                                        Staffelpreise: {addon.price_tiers.map((tier, i) => (
                                                                            <span key={i}>
                                                                                {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'}: {tier.price_per_unit}€
                                                                                {i < addon.price_tiers.length - 1 ? ' | ' : ''}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Quantity Selector */}
                                                                {isSelected && hasQuantity && (
                                                                    <div className="flex items-center gap-2 mt-2">
                                                                        <span className="text-xs text-gray-500">Anzahl:</span>
                                                                        <div className="flex items-center border rounded">
                                                                            <button 
                                                                                onClick={() => updateAddonQuantity(addon._id, quantity - 1)}
                                                                                className="p-1 hover:bg-gray-100"
                                                                                disabled={quantity <= (addon.min_quantity || 1)}
                                                                            >
                                                                                <Minus size={14} />
                                                                            </button>
                                                                            <input 
                                                                                type="number"
                                                                                value={quantity}
                                                                                onChange={(e) => updateAddonQuantity(addon._id, parseInt(e.target.value) || 1)}
                                                                                className="w-12 text-center text-sm border-x py-1"
                                                                                min={addon.min_quantity || 1}
                                                                                max={addon.max_quantity || 100}
                                                                            />
                                                                            <button 
                                                                                onClick={() => updateAddonQuantity(addon._id, quantity + 1)}
                                                                                className="p-1 hover:bg-gray-100"
                                                                                disabled={addon.max_quantity && quantity >= addon.max_quantity}
                                                                            >
                                                                                <Plus size={14} />
                                                                            </button>
                                                                        </div>
                                                                        <span className="text-sm font-medium">
                                                                            = {calculateAddonPrice(addon, quantity).toFixed(2)}€
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Special Requests */}
                                <div className="mt-6 pt-4 border-t">
                                    <label className="block font-medium mb-2">
                                        Besondere Wünsche oder Fragen (optional)
                                    </label>
                                    <Textarea
                                        value={specialRequests}
                                        onChange={(e) => setSpecialRequests(e.target.value)}
                                        placeholder="z.B. besondere Bestuhlung, spezielle Anforderungen, Fragen zum Ablauf..."
                                        className="min-h-[80px] resize-none"
                                    />
                                </div>

                                {/* Price with VAT and Discount */}
                                <div className="mt-6 pt-4 border-t">
                                    {user?.role === 'internal' || user?.role === 'admin' ? (
                                        <div>
                                            <div className="flex justify-between items-center">
                                                <span className="font-semibold">Gesamtpreis</span>
                                                <span className="text-2xl font-bold text-green-600">Kostenlos</span>
                                            </div>
                                            <p className="text-xs text-green-600 mt-1">Als interner Nutzer ist die Raumbuchung kostenlos</p>
                                        </div>
                                    ) : calculatedPrice > 0 ? (
                                        <div className="space-y-2">
                                            {/* Discount Code Input */}
                                            <div className="bg-gray-50 p-3 rounded-lg mb-4">
                                                <label className="text-sm font-medium text-gray-700 block mb-2 flex items-center gap-2">
                                                    <Gift className="w-4 h-4" /> Rabattcode
                                                </label>
                                                <div className="flex gap-2">
                                                    <Input
                                                        value={discountCode}
                                                        onChange={(e) => {
                                                            setDiscountCode(e.target.value.toUpperCase());
                                                            setDiscountValidation(null);
                                                        }}
                                                        placeholder="z.B. WELCOME-ABC123"
                                                        className="flex-1 uppercase"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={validateDiscountCode}
                                                        disabled={!discountCode.trim() || validatingDiscount}
                                                        className="whitespace-nowrap"
                                                    >
                                                        {validatingDiscount ? 'Prüft...' : 'Einlösen'}
                                                    </Button>
                                                </div>
                                                {discountValidation?.valid && (
                                                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700 flex items-center gap-2">
                                                        <Check size={16} />
                                                        {discountValidation.message} (-{discountValidation.discount_amount.toFixed(2)}€)
                                                    </div>
                                                )}
                                                <p className="text-xs text-gray-400 mt-2">
                                                    Rabattcodes gelten nur für Raumkosten, nicht für Zusatzoptionen.
                                                </p>
                                            </div>
                                            
                                            {/* Room price */}
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-600">Raumkosten (netto)</span>
                                                <span>{(calculatedPrice - addonsTotal).toFixed(2)}€</span>
                                            </div>
                                            
                                            {/* Discount (if applied) */}
                                            {discountValidation?.valid && (
                                                <div className="flex justify-between text-sm text-green-600">
                                                    <span>Rabatt ({discountValidation.discount_percent}%)</span>
                                                    <span>-{discountValidation.discount_amount.toFixed(2)}€</span>
                                                </div>
                                            )}
                                            
                                            {/* Addons total */}
                                            {addonsTotal > 0 && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Zusatzleistungen (netto)</span>
                                                    <span>{addonsTotal.toFixed(2)}€</span>
                                                </div>
                                            )}
                                            
                                            {/* Net subtotal */}
                                            <div className="flex justify-between text-sm pt-2 border-t border-dashed">
                                                <span className="text-gray-600">Zwischensumme (netto)</span>
                                                <span>{finalNetPrice.toFixed(2)}€</span>
                                            </div>
                                            
                                            {/* VAT */}
                                            <div className="flex justify-between text-sm">
                                                <span className="text-gray-600">zzgl. 19% MwSt.</span>
                                                <span>{(finalNetPrice * 0.19).toFixed(2)}€</span>
                                            </div>
                                            
                                            {/* Gross total */}
                                            <div className="flex justify-between items-center pt-2 border-t border-dashed">
                                                <span className="font-semibold">Gesamtpreis (brutto)</span>
                                                <span className="text-2xl font-bold">{(finalNetPrice * 1.19).toFixed(2)}€</span>
                                            </div>
                                            
                                            {discountValidation?.valid && (
                                                <div className="bg-green-50 text-green-700 text-xs p-2 rounded mt-2 text-center flex items-center justify-center gap-1">
                                                    <Check className="w-3 h-3" /> Sie sparen {(discountValidation.discount_amount * 1.19).toFixed(2)}€ (brutto)!
                                                </div>
                                            )}
                                            
                                            <p className="text-xs text-gray-500 mt-1">
                                                Alle Preise für Raumbuchungen und Zusatzleistungen verstehen sich zzgl. 19% MwSt.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold">Gesamtpreis</span>
                                            <span className="text-2xl font-bold">0,00€</span>
                                        </div>
                                    )}
                                </div>

                                {/* Cancellation Notice with dynamic rules */}
                                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm">
                                            <div className="font-semibold text-amber-800">Stornierungsbedingungen</div>
                                            {cancellationRules?.tiers?.length > 0 ? (
                                                <ul className="text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                                                    {[...cancellationRules.tiers]
                                                        .sort((a, b) => b.days_before - a.days_before)
                                                        .map((tier, index) => (
                                                            <li key={index}>
                                                                {tier.fee_percent === 0 
                                                                    ? `Mehr als ${tier.days_before} Tage vor Termin: Kostenlose Stornierung`
                                                                    : `${tier.days_before}+ Tage vor Termin: ${tier.fee_percent}% Stornierungsgebühr`
                                                                }
                                                            </li>
                                                        ))
                                                    }
                                                    <li>
                                                        Weniger als {Math.min(...cancellationRules.tiers.map(t => t.days_before))} Tage: <strong>{cancellationRules.default_fee_percent}% Stornierungsgebühr</strong>
                                                    </li>
                                                </ul>
                                            ) : (
                                                <p className="text-amber-700 mt-1">
                                                    Bei Stornierung oder Nichterscheinen (No-Show) werden <strong>100% des Buchungsbetrags</strong> berechnet.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <Button 
                                    className="w-full mt-6 py-6 text-lg bg-black hover:bg-gray-800"
                                    onClick={handleSubmit}
                                    disabled={submitting || !startTime || !endTime || !openingHours.is_open}
                                >
                                    {submitting ? 'Wird gebucht...' : 'Jetzt buchen'}
                                </Button>
                                <p className="text-xs text-gray-500 text-center mt-3">
                                    Ihre Angaben verwenden wir zur Abwicklung der Buchung. Details in der{' '}
                                    <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Addon Info Dialog */}
            <Dialog open={!!selectedAddonInfo} onOpenChange={() => setSelectedAddonInfo(null)}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl">{selectedAddonInfo?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Gallery */}
                        {selectedAddonInfo?.gallery_images?.length > 0 && (
                            <div className="grid grid-cols-2 gap-2">
                                {selectedAddonInfo.gallery_images.map((img, i) => (
                                    <img 
                                        key={i} 
                                        src={img} 
                                        alt={`${selectedAddonInfo.name} ${i + 1}`}
                                        className="w-full h-32 object-cover rounded-lg"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                ))}
                            </div>
                        )}
                        
                        {/* Description */}
                        <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: selectedAddonInfo?.detailed_description }}
                        />
                        
                        {/* Price Info */}
                        {selectedAddonInfo?.price_type === 'tiered' && selectedAddonInfo?.price_tiers && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-semibold mb-2">Staffelpreise</h4>
                                <div className="space-y-1 text-sm">
                                    {selectedAddonInfo.price_tiers.map((tier, i) => (
                                        <div key={i} className="flex justify-between">
                                            <span>
                                                {tier.min_quantity}{tier.max_quantity ? `-${tier.max_quantity}` : '+'} {selectedAddonInfo.unit_name || 'Stück'}
                                            </span>
                                            <span className="font-medium">{tier.price_per_unit}€ / {selectedAddonInfo.unit_name || 'Stück'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Auth Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-md">
                        <CardContent className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold">
                                    {authMode === 'login' ? 'Anmelden' : 'Registrieren'}
                                </h3>
                                <button onClick={() => setShowAuthModal(false)}>
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleAuth} className="space-y-4">
                                {authMode === 'register' && (
                                    <>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Vorname</label>
                                                <Input 
                                                    value={authForm.first_name}
                                                    onChange={(e) => setAuthForm({...authForm, first_name: e.target.value})}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium block mb-1">Nachname</label>
                                                <Input 
                                                    value={authForm.last_name}
                                                    onChange={(e) => setAuthForm({...authForm, last_name: e.target.value})}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium block mb-1">Firma (optional)</label>
                                            <Input 
                                                value={authForm.company}
                                                onChange={(e) => setAuthForm({...authForm, company: e.target.value})}
                                            />
                                        </div>
                                    </>
                                )}
                                
                                <div>
                                    <label className="text-sm font-medium block mb-1">E-Mail</label>
                                    <Input 
                                        type="email"
                                        value={authForm.email}
                                        onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium block mb-1">Passwort</label>
                                    <Input 
                                        type="password"
                                        value={authForm.password}
                                        onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                                        required
                                    />
                                </div>

                                <Button type="submit" className="w-full bg-black hover:bg-gray-800">
                                    {authMode === 'login' ? 'Anmelden' : 'Registrieren'}
                                </Button>
                                <p className="text-xs text-gray-500 text-center">
                                    Hinweise zur Verarbeitung Ihrer Daten in der{' '}
                                    <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
                                </p>
                            </form>

                            <div className="mt-4 text-center text-sm">
                                {authMode === 'login' ? (
                                    <span>
                                        Noch kein Konto?{' '}
                                        <button 
                                            className="text-black font-semibold underline"
                                            onClick={() => setAuthMode('register')}
                                        >
                                            Jetzt registrieren
                                        </button>
                                    </span>
                                ) : (
                                    <span>
                                        Bereits registriert?{' '}
                                        <button 
                                            className="text-black font-semibold underline"
                                            onClick={() => setAuthMode('login')}
                                        >
                                            Anmelden
                                        </button>
                                    </span>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default RoomBookingPage;
